import { useCallback, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, AlertCircle } from 'lucide-react';
import type { ImportPlanReport } from '@kreart/shared';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { describeError, describeServerSentence } from '@/lib/form/errorMessages';

interface XlsxDropzoneProps {
  open: boolean;
  onClose: () => void;
  /** Translation namespace label (e.g. 'materials' or 'edges') for headings. */
  entityLabel: string;
  acceptedTypes?: string;
  /** Called when admin picks a file. Should call the dry-run import API. */
  onPreview: (file: File) => Promise<ImportPlanReport>;
  /** Called when admin confirms. Should call apply-import API. */
  onConfirm: (file: File) => Promise<void>;
}

const DEFAULT_ACCEPT = '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Drag-and-drop xlsx upload dialog with a preview-then-confirm flow.
 *
 * Step 1: admin drops a file → parent runs a dry-run import and returns the
 * `ImportPlanReport`. We render per-row outcomes grouped by action.
 * Step 2: confirm is disabled when `errors > 0`. On confirm, parent runs the
 * apply-import API.
 */
export function XlsxDropzone({
  open,
  onClose,
  entityLabel,
  acceptedTypes = DEFAULT_ACCEPT,
  onPreview,
  onConfirm,
}: XlsxDropzoneProps) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<ImportPlanReport | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setFile(null);
    setReport(null);
    setPreviewError(null);
    setBusy(false);
    setConfirming(false);
  };

  const handleFile = useCallback(
    async (f: File) => {
      const ok =
        f.name.toLowerCase().endsWith('.xlsx') ||
        f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      if (!ok) {
        setPreviewError(
          t('catalogAdmin.dropzone.unsupportedType', {
            defaultValue: 'Only .xlsx files are supported.',
          }),
        );
        return;
      }
      setFile(f);
      setReport(null);
      setPreviewError(null);
      setBusy(true);
      try {
        const r = await onPreview(f);
        setReport(r);
      } catch (err) {
        setPreviewError(describeError(t, err));
      } finally {
        setBusy(false);
      }
    },
    [onPreview, t],
  );

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void handleFile(f);
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) void handleFile(f);
  };

  const onConfirmClick = async () => {
    if (!file || !report || report.totals.errors > 0) return;
    setConfirming(true);
    try {
      await onConfirm(file);
      reset();
      onClose();
    } catch (err) {
      setPreviewError(describeError(t, err));
    } finally {
      setConfirming(false);
    }
  };

  const close = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      {/* A dropped file is as much a pending import as a picked one. */}
      <DialogContent className="max-w-3xl" unsavedChanges={file !== null}>
        <div className="flex items-center justify-between border-b border-border pb-2">
          <h2 className="text-lg font-semibold">
            {t('catalogAdmin.dropzone.title', {
              defaultValue: 'Import {{entity}}',
              entity: entityLabel,
            })}
          </h2>
        </div>

        {!file ? (
          <div
            data-testid="xlsx-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="mt-4 flex h-48 flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border bg-muted/40 text-sm text-muted-foreground"
          >
            <Upload className="h-8 w-8" />
            <p>
              {t('catalogAdmin.dropzone.hint', {
                defaultValue: 'Drag and drop an xlsx file or click to browse.',
              })}
            </p>
            <Button type="button" variant="outline" onClick={() => inputRef.current?.click()}>
              {t('catalogAdmin.dropzone.browse', { defaultValue: 'Browse files' })}
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept={acceptedTypes}
              className="hidden"
              onChange={onInputChange}
              data-testid="xlsx-dropzone-input"
            />
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <p className="text-sm">
              <span className="font-medium">{file.name}</span> ·{' '}
              <span className="text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</span>
            </p>

            {busy && (
              <p className="text-sm text-muted-foreground">
                {t('catalogAdmin.dropzone.previewing', { defaultValue: 'Generating preview…' })}
              </p>
            )}

            {previewError && (
              <div className="flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>{previewError}</p>
              </div>
            )}

            {report && <ReportSummary report={report} />}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={close}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button
                type="button"
                onClick={onConfirmClick}
                disabled={busy || confirming || !report || report.totals.errors > 0}
                data-testid="xlsx-dropzone-confirm"
              >
                {confirming
                  ? t('catalogAdmin.dropzone.applying', { defaultValue: 'Applying…' })
                  : t('catalogAdmin.dropzone.confirm', { defaultValue: 'Confirm import' })}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReportSummary({ report }: { report: ImportPlanReport }) {
  const { t } = useTranslation();
  const groups: Array<{
    key: 'create' | 'update' | 'unchanged' | 'error';
    label: string;
    count: number;
  }> = [
    {
      key: 'create',
      label: t('catalogAdmin.dropzone.created', { defaultValue: 'Created' }),
      count: report.totals.created,
    },
    {
      key: 'update',
      label: t('catalogAdmin.dropzone.updated', { defaultValue: 'Updated' }),
      count: report.totals.updated,
    },
    {
      key: 'unchanged',
      label: t('catalogAdmin.dropzone.unchanged', { defaultValue: 'Unchanged' }),
      count: report.totals.unchanged,
    },
    {
      key: 'error',
      label: t('catalogAdmin.dropzone.errors', { defaultValue: 'Errors' }),
      count: report.totals.errors,
    },
  ];
  return (
    <div className="space-y-2" data-testid="xlsx-report">
      <div className="flex flex-wrap gap-3 text-sm">
        {groups.map((g) => (
          <span
            key={g.key}
            className="rounded-md bg-muted px-2 py-1"
            data-testid={`xlsx-total-${g.key}`}
          >
            {g.label}: <strong>{g.count}</strong>
          </span>
        ))}
      </div>
      <div className="max-h-64 overflow-auto rounded-md border border-border">
        {groups.map((g) => {
          if (g.count === 0) return null;
          const rows = report.rows.filter((r) => r.action === g.key);
          return (
            <details key={g.key} open={g.key !== 'unchanged'} className="border-b border-border">
              <summary className="cursor-pointer bg-muted/40 px-3 py-1 text-xs font-medium">
                {g.label} ({g.count})
              </summary>
              <ul className="divide-y divide-border text-xs">
                {rows.slice(0, 50).map((r, idx) => (
                  <li key={`${r.sheet}:${r.rowNumber}:${idx}`} className="px-3 py-1">
                    <span className="text-muted-foreground">
                      {r.sheet}:{r.rowNumber}
                    </span>{' '}
                    —{' '}
                    {[r.identifier, r.message && describeServerSentence(t, r.message)]
                      .filter(Boolean)
                      .join(': ')}
                    {r.changedKeys && r.changedKeys.length > 0 && (
                      <span className="ml-2 text-muted-foreground">
                        [{r.changedKeys.join(', ')}]
                      </span>
                    )}
                  </li>
                ))}
                {rows.length > 50 && (
                  <li className="px-3 py-1 text-muted-foreground">…{rows.length - 50} more</li>
                )}
              </ul>
            </details>
          );
        })}
      </div>
    </div>
  );
}
